import React from 'react';
import { Receipt, FileDown, Loader } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Branded customer-invoice list. Receives the pre-fetched invoices array and a
 * download handler. Renders only when there is at least one record so the
 * parent page can omit the section header on fresh accounts.
 */
const InvoicesTable = ({ invoices, downloadingId, onDownload }) => {
  if (!invoices || invoices.length === 0) return null;

  return (
    <div className="mt-10 bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="billing-invoices-section">
      <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
        <Receipt size={18} className="text-slate-900" />
        <h2 className="text-lg font-chivo font-bold text-slate-900">Invoices</h2>
        <span className="text-xs font-ibm text-slate-500 ml-auto">
          {invoices.length} record{invoices.length === 1 ? '' : 's'}
        </span>
      </div>
      <table className="w-full" data-testid="billing-invoices-table">
        <thead className="bg-slate-50">
          <tr>
            <th className="px-6 py-2.5 text-left text-[10px] font-ibm font-semibold text-slate-500 uppercase tracking-widest">Invoice #</th>
            <th className="px-6 py-2.5 text-left text-[10px] font-ibm font-semibold text-slate-500 uppercase tracking-widest">Plan</th>
            <th className="px-6 py-2.5 text-left text-[10px] font-ibm font-semibold text-slate-500 uppercase tracking-widest">Paid</th>
            <th className="px-6 py-2.5 text-right text-[10px] font-ibm font-semibold text-slate-500 uppercase tracking-widest">Amount</th>
            <th className="px-6 py-2.5 text-right text-[10px] font-ibm font-semibold text-slate-500 uppercase tracking-widest">PDF</th>
          </tr>
        </thead>
        <tbody>
          {invoices.map((inv) => {
            const shortId = inv.session_id.slice(-8).toUpperCase();
            const isDownloading = downloadingId === inv.session_id;
            return (
              <tr
                key={inv.session_id}
                className="border-t border-slate-100 hover:bg-slate-50"
                data-testid={`invoice-row-${shortId}`}
              >
                <td className="px-6 py-3 text-sm font-ibm font-semibold text-slate-900 tabular-nums">{inv.invoice_number}</td>
                <td className="px-6 py-3 text-sm font-ibm text-slate-700 capitalize">{inv.plan_name || inv.plan_id}</td>
                <td className="px-6 py-3 text-sm font-ibm text-slate-600">
                  {inv.paid_at ? new Date(inv.paid_at).toLocaleDateString() : '—'}
                </td>
                <td className="px-6 py-3 text-sm font-ibm text-slate-900 text-right tabular-nums">
                  {(inv.currency || 'USD').toUpperCase()} {Number(inv.amount || 0).toFixed(2)}
                </td>
                <td className="px-6 py-3 text-right">
                  <Button
                    onClick={() => onDownload(inv.session_id, inv.invoice_number)}
                    disabled={isDownloading}
                    data-testid={`invoice-download-${shortId}`}
                    className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm h-8 px-3 text-xs"
                  >
                    {isDownloading ? (
                      <><Loader size={12} className="mr-1.5 animate-spin" /> Generating…</>
                    ) : (
                      <><FileDown size={12} className="mr-1.5" /> Download</>
                    )}
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default InvoicesTable;
